import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from '../components/Header';
import { SubmissionForm } from '../components/SubmissionForm';
import { PostsGallery } from '../components/PostsGallery';
import { VisitorCounter } from '../components/VisitorCounter';
import { Button } from '../components/ui/button';
import { Heart, MessageCircle, X, ChevronDown } from 'lucide-react';

// Emotional people-focused hero image - UAE solidarity
const HERO_VIDEO = "https://player.vimeo.com/external/434045526.sd.mp4?s=c27eecc69a27dbc4ff2b87d38afc35f1a9e7c02d&profile_id=164&oauth2_token_id=57447761";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_uae-support/artifacts/iz4z4hkq_uaeis%20new.png";

export const HomePage = () => {
  const { t, language } = useLanguage();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleSubmissionSuccess = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const scrollToGallery = () => {
    document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Header />
      
      {/* Hero Section with Video Background */}
      <section className="relative h-screen pt-16 overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            id="hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster={HERO_IMAGE}
            className="w-full h-full object-cover"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
            {/* Visitor Counter */}
            <div className="mb-6">
              <VisitorCounter />
            </div>

            {/* Badge with Animated Heart */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-5 py-2.5 rounded-full text-sm font-medium mb-8 border border-white/20">
              <Heart className="w-4 h-4 text-[#CE1126] animate-heartbeat" />
              <span>{t('tagline')}</span>
            </div>
            
            {/* Main Title */}
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-serif font-bold text-white mb-6 leading-tight">
              {t('heroTitle')}
              <span className="text-[#009639]">...</span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-white/80 text-lg sm:text-xl lg:text-2xl max-w-2xl mx-auto mb-12">
              {t('heroSubtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsFormOpen(true)}
                className="bg-[#009639] hover:bg-[#007a2f] text-white px-8 py-6 text-lg font-medium rounded-full transition-all shadow-lg shadow-[#009639]/30 hover:shadow-xl hover:shadow-[#009639]/40"
                data-testid="share-message-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {t('submitButton')}
              </Button>
              <Button
                onClick={scrollToGallery}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg font-medium rounded-full backdrop-blur-sm"
                data-testid="view-messages-btn"
              >
                {t('galleryTitle')}
              </Button>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <button 
              onClick={scrollToGallery}
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Scroll to messages"
            >
              <ChevronDown className="w-8 h-8" />
            </button>
          </div>
        </div>
      </section>

      {/* Posts Gallery Section */}
      <section id="gallery-section" className="py-16 sm:py-24 bg-gray-50" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PostsGallery refreshTrigger={refreshTrigger} />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#009639] flex items-center justify-center">
                <span className="text-white font-serif font-bold text-sm">U</span>
              </div>
              <span className="text-white/60 text-sm">{t('footerText')}</span>
            </div>
            
            <p className="text-white/40 text-xs">
              {language === 'ar' ? 'نقف معاً' : 'Standing Together'}
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Chat Button */}
      {!isFormOpen && (
        <button
          onClick={() => setIsFormOpen(true)}
          className="fixed bottom-32 right-6 z-50 w-14 h-14 md:w-16 md:h-16 bg-[#009639] hover:bg-[#007a2f] text-white rounded-full shadow-xl shadow-[#009639]/40 flex items-center justify-center transition-all hover:scale-110 group"
          data-testid="floating-form-btn"
        >
          <MessageCircle className="w-6 h-6 md:w-7 md:h-7 group-hover:scale-110 transition-transform" />
          {/* Pulse ring */}
          <span className="absolute inset-0 rounded-full bg-[#009639] animate-ping opacity-30" />
          {/* Red dot notification */}
          <span className="absolute -top-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-[#CE1126] rounded-full border-2 border-white" />
        </button>
      )}

      {/* Chatbot-style Form Modal */}
      {isFormOpen && (
        <div 
          className="fixed inset-x-4 bottom-4 md:inset-auto md:bottom-24 md:right-6 z-50 md:w-full md:max-w-md animate-slide-up"
          data-testid="form-modal"
        >
          {/* Form Container */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="bg-[#0a0a0a] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#009639] flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white animate-heartbeat" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{t('formTitle')}</h3>
                  <p className="text-white/60 text-xs">{t('tagline')}</p>
                </div>
              </div>
              <button
                onClick={() => setIsFormOpen(false)}
                className="text-white/60 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                data-testid="close-form-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Content */}
            <div className="max-h-[60vh] md:max-h-[70vh] overflow-y-auto">
              <SubmissionForm onSuccess={handleSubmissionSuccess} compact />
            </div>
          </div>
        </div>
      )}

      {/* Backdrop for mobile */}
      {isFormOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:bg-black/30"
          onClick={() => setIsFormOpen(false)}
        />
      )}
    </div>
  );
};

export default HomePage;
